"use client";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { useTranslation } from "@/hooks/useTranslation.hook";
import { useOTPInput } from "@/hooks/useOTPInput";
import { TwoFAModalProps } from "@/interface/account/two-fa.types";

const TwoFAModal = ({
  isOpen,
  onVerify,
  onResend,
  onClose,
  isVerifying,
  isResending,
  resendDisabled,
  countdown,
}: TwoFAModalProps) => {
  const { t } = useTranslation();
  const {
    code,
    otpInputs,
    handleKeyDown,
    handlePaste,
    handleSubmit,
    formatCountdown,
  } = useOTPInput(isOpen, onVerify);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="sm:max-w-md"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            {t("2-Step Verification")}
          </DialogTitle>
          <DialogDescription className="text-center">
            {t("Enter the 6-digit code sent to your email")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label
              htmlFor="otp-code"
              className="text-sm font-medium text-center block"
            >
              {t("Authentication Code")}
            </label>
            <div id="otp-code" className="flex gap-2 justify-center">
              {[0, 1, 2, 3, 4, 5].map((position) => (
                <Input
                  key={`otp-pos-${position}`}
                  id={`otp-${position}`}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={code[position]}
                  // onChange={(e) => handleInputChange(e, position)}
                  onKeyDown={(e) => handleKeyDown(position, e)}
                  onPaste={(e) => handlePaste(e, position)}
                  ref={(input) => {
                    if (input) {
                      otpInputs.current[position] = input;
                    }
                  }}
                  className="w-12 h-12 text-center text-lg font-semibold"
                  disabled={isVerifying}
                />
              ))}
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              type="submit"
              className="flex-1"
              disabled={code.includes("") || isVerifying}
            >
              {isVerifying && <Spinner size="sm" className="mr-2" />}
              {t("Verify")}
            </Button>

            <Button
              type="button"
              variant="outline"
              onClick={onResend}
              disabled={resendDisabled || isResending}
              className="flex-1"
            >
              {isResending && <Spinner size="sm" className="mr-2" />}
              {resendDisabled
                ? `${t("Resend")} (${formatCountdown(countdown)})`
                : t("Resend")}
            </Button>
          </div>

          <p className="text-xs text-center text-muted-foreground">
            {t("If you don't receive the code, please check your spam folder")}
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default TwoFAModal;
